import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTabVisibility } from '../../hooks/useTabVisibility';
import './TabNavigation.css';

// Import SVG tab icons - matching reference project exactly
import { ReactComponent as GoalsFocusIcon } from '../../assets/icons/tab-bar/goals-focus.svg';
import { ReactComponent as GoalsOutFocusIcon } from '../../assets/icons/tab-bar/goals-out-focus.svg';
import { ReactComponent as CalendarFocusIcon } from '../../assets/icons/tab-bar/calendar-focus.svg';
import { ReactComponent as CalendarOutFocusIcon } from '../../assets/icons/tab-bar/calendar-out-focus.svg';
import { ReactComponent as AnnouncementFocusIcon } from '../../assets/icons/tab-bar/announcement-focus.svg';
import { ReactComponent as AnnouncementOutFocusIcon } from '../../assets/icons/tab-bar/announcement-out-focus.svg';
import { ReactComponent as SettingsFocusIcon } from '../../assets/icons/tab-bar/settings-focus.svg';
import { ReactComponent as SettingsOutFocusIcon } from '../../assets/icons/tab-bar/settings-out-focus.svg';
import { useTranslation } from 'react-i18next';

interface TabNavigationProps {
  isAuthenticated: boolean;
}

interface TabItem {
  path: string;
  label: string;
  focusIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  outFocusIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  exact?: boolean;
}

const TabNavigation: React.FC<TabNavigationProps> = ({ isAuthenticated }) => {
  const location = useLocation();
  const shouldShowTabs = useTabVisibility();
  const { t } = useTranslation();

  // Don't render if not authenticated or if tabs should be hidden
  if (!isAuthenticated || !shouldShowTabs) {
    return null;
  }

  const tabs: TabItem[] = [
    {
      path: '/goals',
      label: t('navigation:tabs.goals'),
      focusIcon: GoalsFocusIcon,
      outFocusIcon: GoalsOutFocusIcon,
      exact: true
    },
    {
      path: '/calendar',
      label: t('navigation:tabs.calendar'),
      focusIcon: CalendarFocusIcon,
      outFocusIcon: CalendarOutFocusIcon,
      exact: true
    },
    {
      path: '/announcements',
      label: t('navigation:tabs.announcements'),
      focusIcon: AnnouncementFocusIcon,
      outFocusIcon: AnnouncementOutFocusIcon,
      exact: true
    },
    {
      path: '/settings',
      label: t('navigation:tabs.settings'),
      focusIcon: SettingsFocusIcon,
      outFocusIcon: SettingsOutFocusIcon,
      exact: true
    }
  ];

  const isActive = (tab: TabItem) => {
    if (tab.exact) {
      return location.pathname === tab.path;
    }
    return location.pathname.startsWith(tab.path);
  };

  return (
    <nav className="tab-navigation">
      <div className="tab-container">
        {tabs.map((tab) => {
          const isTabActive = isActive(tab);
          const IconComponent = isTabActive ? tab.focusIcon : tab.outFocusIcon;
          
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`tab-item ${isTabActive ? 'active' : ''}`}
            >
              <IconComponent className="tab-icon" />
              <span className="tab-label">{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default TabNavigation;
